import { Alert, Button, Select, Skeleton, Space, Tooltip, Typography } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faExclamationCircle, faLocationDot } from "@fortawesome/pro-solid-svg-icons"
import { InvoicingSystem } from "../../../domain/invoicing/settings/invoicingSettings"
import { useInvoicingSettingsQuery } from "../../../domain/invoicing/settings/invoicingSettingsQueries"
import { useGetTaxRatesQuery } from "../../../domain/invoicing/taxRatesQueries"
import { useFeaturesQuery } from "../../../domain/features/featuresQueries"
import { useResolvedTaxRateQuery } from "../../../domain/invoicing/resolvedTaxRateQueries"
import { filterTaxRates, findDefaultTaxRate } from "../../../domain/invoicing/taxRate"
import { hasValue } from "../../../helpers/nullable"
import useFeatureFlag from "../../../hooks/useFeatureFlag"
import { buildTaxRateOptions, fromSelectedTaxRateValue, getAutomaticRateDisplay, shouldAutoSelectDefaultTaxRate, toSelectedTaxRateValue } from "./taxRateSelectorModel"
import { useEffect } from "react"

type Props = {
  invoicingSystem: InvoicingSystem
  selectedTaxRateId: string | null | undefined
  onChange: (taxRateId: string | null | undefined) => void
  canEditGroupFields: boolean
  jobId: number
}

const TaxRateSelector = ({ invoicingSystem, selectedTaxRateId, onChange, canEditGroupFields, jobId }: Props) => {
  const isAutoTaxFeatureEnabled = useFeatureFlag('automatic-tax-rates')
  const featuresQuery = useFeaturesQuery()
  const invoicingSettingsQuery = useInvoicingSettingsQuery()
  const isAutomaticOffered = isAutoTaxFeatureEnabled
    && invoicingSystem === 'synkedUp'
    && invoicingSettingsQuery.data?.automaticTaxIsActive === true
  const isAutomaticSelected = isAutomaticOffered && !hasValue(selectedTaxRateId)

  const resolvedTaxRateQuery = useResolvedTaxRateQuery(jobId, isAutomaticSelected)

  const taxRatesQuery = useGetTaxRatesQuery()
  const taxRates = filterTaxRates(taxRatesQuery.data ?? [], invoicingSystem, selectedTaxRateId)

  useEffect(() => {
    const canAutoSelectDefault = shouldAutoSelectDefaultTaxRate({
      isAutomaticOffered,
      isAutomaticAvailabilityKnown: featuresQuery.data !== undefined && invoicingSettingsQuery.data !== undefined,
      hasSelectedTaxRateId: hasValue(selectedTaxRateId),
    })
    if (!canAutoSelectDefault) {
      return
    }
    const defaultTaxRate = findDefaultTaxRate(taxRates, invoicingSystem)
    if (hasValue(defaultTaxRate)) {
      onChange(defaultTaxRate.id)
    }
  }, [taxRates, selectedTaxRateId, isAutomaticOffered, featuresQuery.data, invoicingSettingsQuery.data])

  const automaticRateDisplay = getAutomaticRateDisplay({
    isAutomaticSelected,
    isResolvedRateLoading: resolvedTaxRateQuery.isLoading,
    isResolvedRateError: resolvedTaxRateQuery.isError,
    resolvedTaxRate: resolvedTaxRateQuery.data,
  })

  if (!isAutomaticOffered && taxRates.length === 0) {
    return <Alert
      type="warning"
      showIcon={true}
      message="No Tax Rates Found"
      description="Add tax rates in Invoicing Settings to start charging sales tax."
      action={<Button type="primary" size="small" onClick={() => window.open('/#!/invoicing-settings', '_blank')}>Add Tax Rates</Button>}
    />
  }

  const select = <Space direction="vertical" size={0}>
    <Select
      options={buildTaxRateOptions(taxRates, isAutomaticOffered)}
      value={toSelectedTaxRateValue(selectedTaxRateId, isAutomaticOffered)}
      onChange={x => onChange(fromSelectedTaxRateValue(x))}
      loading={taxRatesQuery.isLoading}
      disabled={!canEditGroupFields || taxRatesQuery.isLoading || taxRatesQuery.isError}
      popupMatchSelectWidth={false}
      status={automaticRateDisplay.mode === 'unresolved' ? 'warning' : undefined}
    />
    {automaticRateDisplay.mode === 'loading' && <Skeleton.Input active size="small" />}
    {automaticRateDisplay.mode === 'resolved' && (automaticRateDisplay.resolvedLocationText
      ? <>
        <Typography.Text>
          <FontAwesomeIcon icon={faLocationDot} /> {automaticRateDisplay.ratePercent} based on service location: <Typography.Text strong>{automaticRateDisplay.resolvedLocationText}</Typography.Text>
        </Typography.Text>
        {automaticRateDisplay.jurisdiction && <Typography.Text type="secondary">{automaticRateDisplay.jurisdiction}</Typography.Text>}
      </>
      : <Typography.Text type="secondary">
        <FontAwesomeIcon icon={faLocationDot} /> {automaticRateDisplay.ratePercent} based on service location{automaticRateDisplay.jurisdiction ? ` (${automaticRateDisplay.jurisdiction})` : ''}
      </Typography.Text>
    )}
    {automaticRateDisplay.mode === 'resolved' && automaticRateDisplay.locationMismatch && <Alert
      type="warning"
      showIcon
      message="Tax location doesn't match this job's property"
      description={`This rate was calculated for ${automaticRateDisplay.locationMismatch.resolvedLocation}, but the job's property address is in ${automaticRateDisplay.locationMismatch.propertyLocation}. Verify the tax rate before creating this invoice.`}
    />}
    {automaticRateDisplay.mode === 'unresolved' && <Typography.Text type="warning">
      <FontAwesomeIcon icon={faExclamationCircle} /> Location-based rate unavailable — choose a tax rate
    </Typography.Text>}
  </Space>

  return canEditGroupFields
    ? select
    : <Tooltip title="This can only be changed at the group level.">{select}</Tooltip>
}

export default TaxRateSelector
